import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { CapTableSummary } from '@/components/ledger/CapTableSummary';
import ShareRegisterCard from '@/components/ledger/ShareRegisterCard';
import { Company } from './Companies';
import * as CapLedgerService from '@/services/capLedgerService';
import { CompanyOverview } from '@/components/company/CompanyOverview';
import { CompanyShareholders } from '@/components/company/CompanyShareholders';
import { CompanyTransactions } from '@/components/company/CompanyTransactions';

const CompanyDetail = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [capTable, setCapTable] = useState<CapLedgerService.CapTableSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    fetchCompanyData();
  }, [companyId]);

  const fetchCompanyData = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      // Fetch company details
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;
      setCompany(companyData);

      // Fetch cap table data
      const capTableData = await CapLedgerService.getCapTableSummary();
      setCapTable(capTableData.filter((item) => item.company_id === companyId));
    } catch (error: any) {
      console.error('Error fetching company data:', error);
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Re-reads just the cap table after an edit, adjustment or deletion.
   *
   * Deliberately not `fetchCompanyData`: that sets the page-level `loading`,
   * which swaps the whole page for a spinner and unmounts the tab strip — so
   * every edit would throw the user back to the Overview tab. This leaves the
   * page mounted and only replaces the rows.
   */
  const refreshCapTable = async () => {
    if (!companyId) return;
    try {
      const capTableData = await CapLedgerService.getCapTableSummary();
      setCapTable(capTableData.filter((item) => item.company_id === companyId));
    } catch (error: unknown) {
      // The write already succeeded; only the re-read failed. Say so, rather
      // than leaving stale numbers on screen with no explanation.
      console.error('Error refreshing cap table:', error);
      toast.error('Saved, but the cap table could not be refreshed. Reload to see the change.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading company details...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!company) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate('/companies')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Companies
          </Button>
          <div className="text-center text-muted-foreground">Company not found</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{company.name}</h1>
              <p className="text-muted-foreground">
                {company.registration_number && `Registration: ${company.registration_number}`}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="cap-table">Cap Table</TabsTrigger>
            <TabsTrigger value="shareholders">Shareholders</TabsTrigger>
            <TabsTrigger value="share-register">Share Register</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <CompanyOverview company={company} capTable={capTable} />
          </TabsContent>

          <TabsContent value="cap-table" className="space-y-6">
            {/* onEdit is what makes an edit visible. Without it the dialog
                writes correctly and the table keeps showing the old number,
                which reads as "editing does not work". */}
            <CapTableSummary data={capTable} loading={loading} onEdit={refreshCapTable} />
          </TabsContent>

          <TabsContent value="shareholders" className="space-y-6">
            <CompanyShareholders companyId={companyId!} />
          </TabsContent>

          <TabsContent value="share-register" className="space-y-6">
            <ShareRegisterCard companyId={companyId!} />
          </TabsContent>

          <TabsContent value="transactions" className="space-y-6">
            <CompanyTransactions companyId={companyId!} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default CompanyDetail;
